import React from 'react';
import { PhotoPlaceholder } from './Placeholders';
interface CarouselSlide {
  image?: string;
  alt?: string;
  placeholder?: string;
  rotate?: boolean;
}
interface CarouselProps {
  slides: CarouselSlide[];
  ariaLabel: string;
  direction?: 'left' | 'right';
}
function Carousel({
  slides,
  ariaLabel,
  direction = 'left'
}: CarouselProps) {
  // Duplicate slides so the marquee can loop seamlessly
  const loop = [...slides, ...slides];
  const animationClass = direction === 'right' ? 'animate-marquee-right' : 'animate-marquee-left';
  return <div className="relative overflow-hidden -mx-6 md:mx-0" aria-label={ariaLabel} role="region" data-id="element-114">
      <div className={`flex w-max gap-4 md:gap-6 ${animationClass}`} data-id="element-115">
        {loop.map((slide, i) => <div key={i} className="shrink-0 w-[70vw] sm:w-[45vw] md:w-[32vw] lg:w-[24vw] max-w-[420px]" aria-hidden={i >= slides.length ? 'true' : undefined} data-id="element-116">
            {slide.image ? <img src={slide.image} alt={slide.alt ?? `Slide ${i % slides.length + 1}`} className={`w-full aspect-square object-cover${slide.rotate ? ' rotate-180' : ''}`} data-id="element-117" /> : <PhotoPlaceholder text={slide.placeholder ?? `FOTO ${i % slides.length + 1}`} className="w-full aspect-square" data-id="element-118" />}
          </div>)}
      </div>
    </div>;
}
interface BadgeProps {
  children: React.ReactNode;
}
function Badge({
  children
}: BadgeProps) {
  return <span className="inline-block bg-marron text-crema font-labrada text-sm tracking-widest uppercase px-4 py-2 rounded-sm" data-id="element-119">
      {children}
    </span>;
}
interface SpecListProps {
  items: string[];
}
function SpecList({
  items
}: SpecListProps) {
  return <ul className="space-y-3 font-labrada text-marron text-base md:text-lg" data-id="element-120">
      {items.map((item, i) => <li key={i} className="flex items-start gap-3" data-id="element-121">
          <span className="text-marron mt-1.5 text-xs leading-none" data-id="element-122">●</span>
          <span data-id="element-123">{item}</span>
        </li>)}
    </ul>;
}
const PRENDAS_GRUPO_1: CarouselSlide[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/oaKm5wcuBMmVntsyCWUa2U/RUANA_P6.png',
  alt: 'Ruana marrón tejida a mano con flores bordadas y pompones de colores'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/8cY4uu4hBMYbMcChY4XwZq/PONCHO_P6.png',
  alt: 'Poncho marrón y crema con diseño geométrico y pompones'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/diVUK4v8QM7XAJJwCvptT8/PONCHO_2_P6.png',
  alt: 'Poncho color crema con franjas marrón y turquesa, con flecos'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/nt3YfHDfte374CeVJdG3Co/Poncho_3.png',
  alt: 'Poncho marrón con diseño pied-de-poule y flecos largos'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/eE1NAXCMYfq4SMEfxqLiJp/CHALINA_P6.png',
  alt: 'Chalina turquesa tejida a mano con motivos bordados y flecos'
}];
const PRENDAS_GRUPO_2: CarouselSlide[] = [{
  image: 'https://cdn.magicpatterns.com/uploads/921Y7xQVwTXRfu87m9SsZN/GORROS_2_P6.png',
  alt: 'Chulo con orejeras y gorro tejidos a mano en lana marrón con motivos blancos'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/jpERCqfa3V7VGRBXQP6UWx/POLAINAS.png',
  alt: 'Polainas tejidas en lana cruda con llamas bordadas y pompones',
  rotate: true
}, {
  image: 'https://cdn.magicpatterns.com/uploads/t3LqW6xAqMRoyT6qx6ECXT/MEDIAS.png',
  alt: 'Manopla y media de lana marrón tejidas a mano con detalle de rombos'
}, {
  image: 'https://cdn.magicpatterns.com/uploads/f2rYPDsQw5tTUeKqW4W5R4/Bufanda.png',
  alt: 'Bufanda tejida a mano en lana color tierra con flecos largos'
}];
const INSTRUMENTOS = [{
  nombre: 'Charango'
}, {
  nombre: 'Sikus'
}, {
  nombre: 'Quena'
}, {
  nombre: 'Erke'
}];
export function Productos() {
  return <section id="productos" className="tex-crema py-12 md:py-24 px-6 md:px-12" data-id="element-124">
      <div className="max-w-7xl mx-auto" data-id="element-125">
        <h2 className="font-labrada font-bold text-4xl md:text-6xl text-negro mb-12 md:mb-20 text-center" data-id="element-126">
          Nuestras Piezas
        </h2>

        {/* PRODUCTO ESTRELLA — Tapices */}
        <article className="mb-16 md:mb-32" data-id="element-127">
          <img src="https://cdn.magicpatterns.com/uploads/nxrWaGLFGQ9TmXd156cDTX/TAPICES_P4.png" alt="Tres tapices andinos tejidos a mano colgados con sus diseños geométricos característicos" className="w-full aspect-[16/9] md:aspect-[21/9] object-cover mb-10" data-id="element-128" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16" data-id="element-129">
            <div data-id="element-130">
              <Badge data-id="element-131">HERENCIA FAMILIAR</Badge>
              <h3 className="font-labrada font-bold text-4xl md:text-5xl text-negro mt-6 mb-6" data-id="element-132">
                Tapices
              </h3>
              <p className="font-labrada text-negro text-lg leading-relaxed" data-id="element-133">
                Tejidos a mano en telar tradicional con lana de llama 100%
                natural. Cada tapiz lleva figuras y símbolos andinos de la
                Quebrada de Humahuaca. Están hechos para ser vividos: funcionan
                como tapiz de pared y también como alfombra. No son solo
                decoración, son piezas para usar todos los días.
              </p>
            </div>
            <div className="md:pt-20" data-id="element-134">
              <SpecList items={['Fibra: lana de llama 100% natural', 'Tintes naturales de la Quebrada', 'Figuras y símbolos andinos', 'Apto como alfombra y tapiz de pared']} data-id="element-135" />
            </div>
          </div>
        </article>

        <div className="h-px bg-marron/40 mb-16 md:mb-32" aria-hidden="true" data-id="element-136"></div>

        {/* PRODUCTO DIFERENCIAL — Tulmas */}
        <article className="mb-16 md:mb-32" data-id="element-137">
          <img src="https://cdn.magicpatterns.com/uploads/4HxSUErDCCfEQVK2bpmngd/TULMAS_P5.png" alt="Dos racimos de tulmas — pompones de lana en tonos tierra y colores vivos colgados de perchas de madera" className="w-full aspect-[16/9] md:aspect-[21/9] object-cover mb-10" data-id="element-138" />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-10 md:gap-16" data-id="element-139">
            <div data-id="element-140">
              <Badge data-id="element-141">Tradición del Norte</Badge>
              <h3 className="font-labrada font-bold text-4xl md:text-5xl text-negro mt-6 mb-6" data-id="element-142">
                Tulmas
              </h3>
              <p className="font-labrada text-negro text-lg leading-relaxed" data-id="element-143">
                Las Tulmas son tiras de pompones de colores hechas a mano,
                originalmente con lana de llama y oveja. Representan la alegría
                en el hogar y acompañan las ceremonias del norte argentino como
                el Carnaval y la Pachamama. Las nuestras son elaboradas por la
                mamá de Tupac con tintes naturales y colores vivos. Se usan para
                adornar la casa, para ceremonias, para colgar en el sombrero y
                para todo lo que traiga alegría.
              </p>
            </div>
            <div className="md:pt-20" data-id="element-144">
              <SpecList items={['Pompones hechos a mano', 'Lana de llama y oveja', 'Tintes naturales y colores vivos', 'Para el hogar, ceremonias y carnaval']} data-id="element-145" />
            </div>
          </div>
        </article>

        <div className="h-px bg-marron/40 mb-12 md:mb-20" aria-hidden="true" data-id="element-146"></div>

        {/* PRENDAS DE VESTIR */}
        <div className="mb-16 md:mb-32" data-id="element-147">
          <h3 className="font-labrada font-bold text-3xl md:text-4xl text-negro mb-10 md:mb-16 text-center" data-id="element-148">
            Prendas de Vestir
          </h3>

          {/* Grupo 1 — Ruanas, Ponchos y Chalinas */}
          <div className="mb-12 md:mb-20" data-id="element-149">
            <h4 className="font-labrada font-bold text-2xl md:text-3xl text-negro mb-3" data-id="element-150">
              Ruanas, Ponchos y Chalinas
            </h4>
            <p className="font-labrada text-negro/80 text-lg leading-relaxed mb-8" data-id="element-151">
              Fibras 100% naturales de llama, oveja y vicuña. Hecho para abrigar
              y vestir todos los días.
            </p>
            <Carousel slides={PRENDAS_GRUPO_1} ariaLabel="Carrusel de Ruanas, Ponchos y Chalinas" direction="right" data-id="element-152" />
          </div>

          {/* Grupo 2 — Chulos, Polainas, Medias y Bufandas */}
          <div data-id="element-153">
            <h4 className="font-labrada font-bold text-2xl md:text-3xl text-negro mb-3" data-id="element-154">
              Chulos, Polainas, Medias y Bufandas
            </h4>
            <p className="font-labrada text-negro/80 text-lg leading-relaxed mb-8" data-id="element-155">
              Fibras 100% naturales de llama, oveja y vicuña. Hecho para abrigar
              y vestir todos los días.
            </p>
            <Carousel slides={PRENDAS_GRUPO_2} ariaLabel="Carrusel de Chulos, Polainas, Medias y Bufandas" direction="left" data-id="element-156" />
          </div>
        </div>

        <div className="h-px bg-marron/40 mb-12 md:mb-20" aria-hidden="true" data-id="element-157"></div>

        {/* INSTRUMENTOS Y ARTESANÍAS */}
        <div data-id="element-158">
          <h3 className="font-labrada font-bold text-3xl md:text-4xl text-negro mb-8 md:mb-12 text-center" data-id="element-159">
            Instrumentos y Artesanías
          </h3>
          <div className="grid grid-cols-1 gap-y-10 md:gap-y-16" data-id="element-160">
            <div className="flex flex-col" data-id="element-161">
              <img src="https://cdn.magicpatterns.com/uploads/ijJ1rfDaGVhiepXs62fyP5/INSTRUMENTOS_P7.png" alt="Conjunto de instrumentos tradicionales andinos: sikus, quenas y erkes en madera y caña" className="max-w-full h-auto mb-6 mx-auto" data-id="element-162" />
              <h4 className="font-labrada font-bold text-2xl text-negro mb-2" data-id="element-163">
                Instrumentos
              </h4>
              <p className="font-labrada text-negro/80 text-lg leading-relaxed" data-id="element-164">
                Instrumentos tradicionales de la Quebrada de Humahuaca,
                conseguidos directamente de artesanos de la comunidad local.
              </p>
            </div>
            <div className="flex flex-col" data-id="element-165">
              <img src="https://cdn.magicpatterns.com/uploads/5TEJJWYXJjz6q6RkLgLVM6/ARTESANIAS.png" alt="Cuatro piezas de cerámica artesanal en arcilla — vasijas y cuenco de la Quebrada de Humahuaca" className="max-w-full h-auto mb-6 mx-auto" data-id="element-166" />
              <h4 className="font-labrada font-bold text-2xl text-negro mb-2" data-id="element-167">
                Artesanías
              </h4>
              <p className="font-labrada text-negro/80 text-lg leading-relaxed" data-id="element-168">
                Artesanías de la Quebrada de Humahuaca, conseguidas directamente
                de artesanos de la comunidad local.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>;
}